/**
 * ARK Reviews — marquee renderer.
 * Loads central ark-reviews.json (shared per page), filters by mode/filter,
 * builds alternating-direction rows with duplicated tracks for seamless scroll,
 * pauses individual rows on hover/focus, supports pointer drag with seamless loop,
 * respects prefers-reduced-motion.
 *
 * Root: [data-ark-reviews]
 */
(function () {
  var ROOT_SELECTOR = '[data-ark-reviews]';
  var DEFAULT_ERROR = 'Recenzije trenutno nisu dostupne. Pokušajte ponovo kasnije.';

  /** @type {Map<string, Promise<Array>>} */
  var jsonCache = new Map();

  /**
   * Fetch reviews JSON once per src per page.
   * @param {string} src
   * @returns {Promise<Array>}
   */
  function loadReviews(src) {
    if (!jsonCache.has(src)) {
      jsonCache.set(
        src,
        fetch(src, { credentials: 'same-origin' }).then(function (response) {
          if (!response.ok) throw new Error('reviews_fetch_failed');
          return response.json();
        }).then(function (data) {
          return Array.isArray(data) ? data : [];
        })
      );
    }
    return /** @type {Promise<Array>} */ (jsonCache.get(src));
  }

  /**
   * @param {unknown} value
   * @returns {number}
   */
  function clampRating(value) {
    var rating = parseInt(String(value), 10);
    if (isNaN(rating)) return 5;
    if (rating < 1) return 1;
    if (rating > 5) return 5;
    return rating;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  function firstInitial(value) {
    var s = String(value || '').trim();
    if (!s) return '?';
    return s.charAt(0).toUpperCase();
  }

  /**
   * Filter the dataset by reviews_filter attribute.
   * @param {Array<Record<string, unknown>>} reviews
   * @param {string} filterKey
   * @returns {Array<Record<string, unknown>>}
   */
  function filterReviews(reviews, filterKey) {
    var clean = reviews.filter(function (item) {
      return item && typeof item === 'object';
    });
    if (filterKey === 'featured') {
      return clean.filter(function (r) { return r.featured === true; });
    }
    if (filterKey === 'homepage') {
      return clean.filter(function (r) { return r.homepage === true; });
    }
    if (filterKey === 'positive') {
      return clean.filter(function (r) { return r.sentiment === 'positive'; });
    }
    return clean;
  }

  /**
   * Fisher–Yates shuffle (deterministic per call; we only care about visual mix).
   * @template T
   * @param {T[]} arr
   * @returns {T[]}
   */
  function shuffle(arr) {
    var copy = arr.slice();
    for (var i = copy.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
    }
    return copy;
  }

  /**
   * Split reviews into N roughly equal rows (round-robin keeps adjacent rows visually distinct).
   * @param {Array<Record<string, unknown>>} reviews
   * @param {number} rowCount
   * @returns {Array<Array<Record<string, unknown>>>}
   */
  function splitIntoRows(reviews, rowCount) {
    /** @type {Array<Array<Record<string, unknown>>>} */
    var rows = [];
    for (var i = 0; i < rowCount; i += 1) rows.push([]);
    reviews.forEach(function (review, index) {
      rows[index % rowCount].push(review);
    });
    return rows.filter(function (row) { return row.length > 0; });
  }

  /**
   * Create an element with optional class and text content.
   * @param {ParentNode} parent
   * @param {string} tag
   * @param {string} [className]
   * @param {string} [text]
   * @returns {HTMLElement}
   */
  function createEl(parent, tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    parent.appendChild(el);
    return el;
  }

  /**
   * @param {ParentNode} parent
   * @param {number} rating
   */
  function appendStars(parent, rating) {
    var stars = createEl(parent, 'span', 'ark-reviews__stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', rating + ' od 5 zvezdica');
    for (var i = 1; i <= 5; i += 1) {
      var star = createEl(stars, 'span', 'ark-reviews__star');
      star.setAttribute('aria-hidden', 'true');
      star.textContent = '★';
      if (i <= rating) star.classList.add('ark-reviews__star--on');
    }
  }

  /**
   * Build a single review card (header: avatar + name, stars below, body below that).
   * @param {Record<string, unknown>} review
   * @returns {HTMLElement}
   */
  function buildCard(review) {
    var author = String(review.author || '').trim();
    var body = String(review.body || '').trim();
    var rating = clampRating(review.rating);

    var card = document.createElement('article');
    card.className = 'ark-reviews__card';

    var head = createEl(card, 'div', 'ark-reviews__card-head');
    var avatar = createEl(head, 'span', 'ark-reviews__avatar');
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = firstInitial(author);

    var authorBlock = createEl(head, 'div', 'ark-reviews__author-block');
    if (author) {
      createEl(authorBlock, 'p', 'ark-reviews__author-name', author);
    }
    appendStars(authorBlock, rating);

    if (body) {
      createEl(card, 'p', 'ark-reviews__card-body', body);
    }

    return card;
  }

  var DRAG_CLICK_THRESHOLD = 5;
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * @param {HTMLElement} el
   * @returns {number}
   */
  function getTranslateX(el) {
    var transform = window.getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;

    if (transform.startsWith('matrix3d(')) {
      var m3 = transform.slice(9, -1).split(',').map(parseFloat);
      return m3[12] || 0;
    }
    if (transform.startsWith('matrix(')) {
      var m2 = transform.slice(7, -1).split(',').map(parseFloat);
      return m2[4] || 0;
    }
    return 0;
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  function getLoopWidth(track) {
    return track.scrollWidth / 2;
  }

  /**
   * @param {number} offsetX
   * @param {number} loopWidth
   * @returns {number}
   */
  function wrapOffset(offsetX, loopWidth) {
    if (loopWidth <= 0) return 0;
    offsetX = ((offsetX % loopWidth) + loopWidth) % loopWidth;
    if (offsetX > 0) offsetX -= loopWidth;
    return offsetX;
  }

  /**
   * @param {HTMLElement} track
   * @param {number} offsetX
   */
  function setTrackTransform(track, offsetX) {
    track.style.transform = 'translate3d(' + offsetX + 'px, 0, 0)';
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} row
   * @param {number} offsetX
   */
  function resumeAutoScroll(track, row, offsetX) {
    var loopWidth = getLoopWidth(track);
    if (loopWidth <= 0) return;

    var durationValue = track.style.getPropertyValue('--ark-reviews-row-duration');
    if (!durationValue) {
      durationValue = window.getComputedStyle(track).getPropertyValue('--ark-reviews-row-duration');
    }
    var duration = parseFloat(durationValue) || 60;
    var isReverse = row.classList.contains('ark-reviews__row--reverse');
    var progress = isReverse
      ? (offsetX + loopWidth) / loopWidth
      : Math.abs(offsetX) / loopWidth;
    progress = Math.max(0, Math.min(1, progress));

    track.classList.remove('is-manual');
    track.style.transform = '';
    track.style.animationDelay = '-' + (progress * duration) + 's';
    track.style.animationPlayState = 'running';
  }

  /**
   * Pointer drag handoff: freeze CSS animation, manual transform with loop wrap,
   * resume auto-scroll from current position on pointerleave.
   * @param {HTMLElement} track
   * @param {HTMLElement} row
   */
  function initTrackInteraction(track, row) {
    if (row.dataset.arkReviewsDragInit === 'true') return;
    row.dataset.arkReviewsDragInit = 'true';

    var dragging = false;
    var isManual = false;
    var offsetX = 0;
    var lastX = 0;
    var totalDrag = 0;

    function enterManualMode() {
      isManual = true;
      track.classList.add('is-manual');
      track.style.animationDelay = '';
      track.style.animationPlayState = '';
      setTrackTransform(track, offsetX);
    }

    function resumeIfManual() {
      if (!isManual || dragging) return;
      resumeAutoScroll(track, row, offsetX);
      isManual = false;
    }

    function isPointerOverRow(event) {
      var rect = row.getBoundingClientRect();
      return (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      );
    }

    row.addEventListener('pointerdown', function (event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      dragging = true;
      totalDrag = 0;
      lastX = event.clientX;
      offsetX = wrapOffset(getTranslateX(track), getLoopWidth(track));
      enterManualMode();

      row.classList.add('is-dragging');
      if (typeof row.setPointerCapture === 'function') {
        row.setPointerCapture(event.pointerId);
      }
    });

    row.addEventListener('pointermove', function (event) {
      if (!dragging) return;

      var delta = event.clientX - lastX;

      totalDrag += Math.abs(delta);
      offsetX = wrapOffset(offsetX + delta, getLoopWidth(track));
      setTrackTransform(track, offsetX);
      lastX = event.clientX;
    });

    function endDrag(event) {
      if (!dragging) return;

      dragging = false;
      row.classList.remove('is-dragging');

      if (typeof row.releasePointerCapture === 'function') {
        try {
          row.releasePointerCapture(event.pointerId);
        } catch (err) {
          /* pointer already released */
        }
      }

      if (totalDrag > DRAG_CLICK_THRESHOLD) {
        row.dataset.arkReviewsDragged = 'true';
      }

      if (!isPointerOverRow(event)) {
        resumeIfManual();
      }
    }

    row.addEventListener('pointerup', endDrag);
    row.addEventListener('pointercancel', endDrag);

    row.addEventListener('pointerleave', function () {
      if (!dragging) resumeIfManual();
    });

    row.addEventListener(
      'click',
      function (event) {
        if (row.dataset.arkReviewsDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
          row.dataset.arkReviewsDragged = 'false';
        }
      },
      true
    );

    if (typeof ResizeObserver !== 'undefined') {
      var resizeObserver = new ResizeObserver(function () {
        if (!isManual) return;
        offsetX = wrapOffset(offsetX, getLoopWidth(track));
        setTrackTransform(track, offsetX);
      });
      resizeObserver.observe(track);
    }
  }

  /**
   * @param {ParentNode} container
   */
  function initRowInteractions(container) {
    if (reducedMotionQuery.matches) return;

    container.querySelectorAll('.ark-reviews__row').forEach(function (rowNode) {
      var row = /** @type {HTMLElement} */ (rowNode);
      var track = row.querySelector('.ark-reviews__track');
      if (track instanceof HTMLElement) {
        initTrackInteraction(track, row);
      }
    });
  }

  /**
   * Build a single row with a duplicated track for seamless infinite scroll.
   * @param {Array<Record<string, unknown>>} rowReviews
   * @param {number} rowIndex
   * @param {number} durationSeconds
   * @returns {HTMLElement}
   */
  function buildRow(rowReviews, rowIndex, durationSeconds) {
    var row = document.createElement('div');
    row.className = 'ark-reviews__row';
    if (rowIndex % 2 === 1) row.classList.add('ark-reviews__row--reverse');

    var track = document.createElement('div');
    track.className = 'ark-reviews__track';

    // Per-row duration slightly varies to avoid lockstep visuals.
    var rowDuration = durationSeconds + (rowIndex * 6);
    track.style.setProperty('--ark-reviews-row-duration', rowDuration + 's');

    // Track holds two copies; CSS scrolls -50% so the seam is invisible.
    var setA = document.createDocumentFragment();
    var setB = document.createDocumentFragment();
    rowReviews.forEach(function (review) {
      setA.appendChild(buildCard(review));
      setB.appendChild(buildCard(review));
    });
    track.appendChild(setA);
    track.appendChild(setB);

    row.appendChild(track);
    return row;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function showError(root, message) {
    var status = root.querySelector('[data-ark-reviews-status]');
    var rows = root.querySelector('[data-ark-reviews-rows]');
    if (rows) rows.hidden = true;
    if (!status) return;
    status.hidden = false;
    status.innerHTML = '';
    createEl(/** @type {HTMLElement} */ (status), 'p', 'ark-reviews__error', message);
  }

  /**
   * Update the review-count text on full-page header if data is present.
   * @param {HTMLElement} root
   * @param {number} totalCount
   */
  function updateCountLabel(root, totalCount) {
    var countEl = root.querySelector('[data-ark-reviews-count]');
    if (!countEl) return;
    var existing = String(countEl.textContent || '').trim();
    // Only auto-update when the merchant left a placeholder or empty value.
    if (!existing || /\d/.test(existing) === false) {
      countEl.textContent = 'Bazirano na ' + totalCount + ' recenzija';
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initRoot(root) {
    if (root.dataset.arkReviewsInit === 'true') return;
    root.dataset.arkReviewsInit = 'true';

    var src = root.getAttribute('data-reviews-src');
    var filterKey = root.getAttribute('data-reviews-filter') || 'homepage';
    var rowsAttr = parseInt(root.getAttribute('data-reviews-rows') || '2', 10);
    var rowCount = isNaN(rowsAttr) ? 2 : Math.max(1, rowsAttr);
    var durationAttr = parseInt(root.getAttribute('data-reviews-duration') || '60', 10);
    var duration = isNaN(durationAttr) ? 60 : Math.max(10, durationAttr);
    var errorMessage = root.getAttribute('data-reviews-error') || DEFAULT_ERROR;

    if (!src) {
      showError(root, errorMessage);
      return;
    }

    loadReviews(src).then(function (allReviews) {
      var filtered = filterReviews(allReviews, filterKey);
      updateCountLabel(root, allReviews.length);

      if (filtered.length === 0) {
        showError(root, errorMessage);
        return;
      }

      var ordered = shuffle(filtered);
      var rows = splitIntoRows(ordered, rowCount);

      var rowsContainer = root.querySelector('[data-ark-reviews-rows]');
      var status = root.querySelector('[data-ark-reviews-status]');
      if (!rowsContainer) return;

      rowsContainer.innerHTML = '';
      rows.forEach(function (rowReviews, index) {
        rowsContainer.appendChild(buildRow(rowReviews, index, duration));
      });

      initRowInteractions(rowsContainer);

      if (status) status.hidden = true;
      rowsContainer.hidden = false;
    }).catch(function () {
      showError(root, errorMessage);
    });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (node) {
      initRoot(/** @type {HTMLElement} */ (node));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
